import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/api';
import AuthContext from '../context/AuthContext';

const Loader = () => (
  <div className="flex justify-center items-center">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
    </div>
  </div>
);

const UpdateBalance = () => {
  const { username } = useContext(AuthContext);
  const [regNumber, setRegNumber] = useState('');
  const [form, setForm] = useState('');
  const [term, setTerm] = useState('');
  const [year, setYear] = useState('');
  const [balance, setBalance] = useState('');
  const [balanceType, setBalanceType] = useState('DR');
  const [currency, setCurrency] = useState('USD');
  const [classType, setClassType] = useState('');
  const [classTypes, setClassTypes] = useState([]);
  const [modalMessage, setModalMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingClassTypes, setFetchingClassTypes] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchClassTypes();
  }, []);

  const fetchClassTypes = async () => {
    setFetchingClassTypes(true);
    setError('');
    try {
      const response = await axios.get(`${BASE_URL}/api/class-types`);
      if (response.data && Array.isArray(response.data)) {
        setClassTypes(response.data);
        console.log(response.data);
      } else {
        setError('Invalid response format from server');
        setClassTypes([]);
      }
    } catch (error) {
      console.error('Error fetching class types:', error);
      setError('Failed to fetch class types. Please try again.');
      setClassTypes([]);
    } finally {
      setFetchingClassTypes(false);
    }
  };

  const clearForm = () => {
    setRegNumber('');
    setForm('');
    setTerm('');
    setYear('');
    setBalance('');
    setBalanceType('DR');
    setCurrency('USD');
    setClassType('');
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await axios.put(`${BASE_URL}/balances/update`, {
        reg_number: regNumber,
        form: parseInt(form, 10),
        term: parseInt(term, 10),
        year: parseInt(year, 10),
        balance: parseFloat(balance),
        balance_type: balanceType,
        currency,
        class_type: classType
      });
      setModalMessage('Balance updated successfully');
      setIsModalOpen(true);
      clearForm();
      setLoading(false);
    } catch (err) {
      setModalMessage('Failed to update balance');
      setIsModalOpen(true);
      setLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleUpdate();
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Restrict access based on username
  if (username !== 'sysadmin' && username !== 'financeadmin') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h2>
            <p className="text-gray-600">You do not have the privileges to update the balance. Please contact the system administrator.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Update Balance</h1>
            <div className="h-1 w-full bg-gray-200 mt-2"></div>
          </div>

          {isModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="bg-white rounded-lg shadow-lg max-w-sm w-full mx-4">
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Notification</h3>
                  <p className="text-gray-600">{modalMessage}</p>
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={closeModal}
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleFormSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                <input
                  type="text"
                  value={regNumber}
                  onChange={(e) => setRegNumber(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Form</label>
                <select
                  value={form}
                  onChange={(e) => setForm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Form</option>
                  {[1, 2, 3, 4, 5, 6].map((form) => (
                    <option key={form} value={form}>
                      {form}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Type</label>
                <select
                  value={classType}
                  onChange={(e) => setClassType(e.target.value)}
                  className="w-full text-black px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  required
                  disabled={fetchingClassTypes}
                >
                  <option value="">Select Class Type</option>
                  {classTypes.map((type) => (
                    <option key={type.id} value={type.ClassTypeName}>
                      {type.ClassTypeName}
                    </option>
                  ))}
                </select>
                {fetchingClassTypes && (
                  <div className="mt-2">
                    <Loader />
                  </div>
                )}
                {error && (
                  <p className="mt-2 text-sm text-red-600">{error}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                <select
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Term</option>
                  {[1, 2, 3].map((term) => (
                    <option key={term} value={term}>
                      {term}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Balance</label>
                <input
                  type="number"
                  step="0.01"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Balance Type</label>
                <select
                  value={balanceType}
                  onChange={(e) => setBalanceType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  required
                >
                  <option value="DR">Debit</option>
                  <option value="CR">Credit</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <input
                  type="text"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={clearForm}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Clear Form
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader />
                    <span>Updating...</span>
                  </div>
                ) : (
                  'Update Balance'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateBalance;
